import { useCallback, useState } from 'react'

import Paper from '@mui/material/Paper'

import StyledTable from '@components/StyledTable'
import { toFilterOptions } from '@util/filter'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { useAutosuggest } from '../../../autosuggest/hooks/useAutosuggest'
import { useProjectSelectlists } from '../../../selectlists/hooks/useProjectSelectlists'
import { useStatusConfig } from '../../../status/hooks/useStatusConfig'
import { Page } from '../../../theme/components/Page'
import { PageSection } from '../../../theme/components/PageSection'
import { useHoofdleidingValuelists } from '../../../valuelists/hooks/useHoofdleidingValuelists'
import { useValuelists } from '../../../valuelists/hooks/useValuelists'
import { ProjectOptions } from '../../project/components/options/ProjectOptions'
import { useHerplanGeblokkeerdeWeken } from '../../project/hooks/useHerplanGeblokkeerdeWeken'
import type { HoofdleidingProject } from '../../project/types/HoofdleidingProject'
import { ProjectenFilters } from '../components/ProjectenFilters'
import { ProjectenTableRow } from '../components/ProjectenTableRow'
import { useProjectenSearch } from '../hooks/useProjectenSearch'

const headerCells = [
  { id: 'status', numeric: false, label: '', sort: false, fixed: 10 },
  { id: 'naam', numeric: false, label: 'Project' },
  { id: 'externeReferentie', numeric: false, label: 'Referenties' },
  { id: 'adres', numeric: false, label: 'Adres', fixed: 300 },
  { id: 'gebied', numeric: false, label: 'Type en gebied' },
  { id: 'disciplines', numeric: false, label: 'Disciplines', fixed: 160 },
  {
    id: 'planningChanged',
    numeric: true,
    label: 'Planning gewijzigd',
    sort: true,
  },
  {
    id: 'startUitvoeringWeek',
    numeric: false,
    label: 'Geplande uitvoeringsweek',
    sort: true,
  },
  { id: 'statusText', numeric: false, label: 'Processtap' },
  { id: 'bijlagen', numeric: false, label: 'Mijlpalen', fixed: 120 },
  { id: 'actions', numeric: false, label: '', fixed: 150 },
] as const

export const Projecten = () => {
  const { organisatie } = useAuthenticatedUser()
  const {
    data: { items: projecten, count, skip, limit },
    isLoading,
    isError,
    initialFilters,
    onChangeFilters,
    onChangeSort,
    onChangePage,
    filters,
    sort,
    onReset,
    onRefresh,
  } = useProjectenSearch()
  const { data: valuelists } = useValuelists()
  const { data: hoofdleidingValuelists } = useHoofdleidingValuelists()
  const { data: statusConfig } = useStatusConfig()
  const [selectedProject, setSelectedProject] = useState<HoofdleidingProject>()
  const { data: selectlists } = useProjectSelectlists({
    id: selectedProject?.aanvraagID || '',
  })
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>()
  const [searchText, setSearchText] = useState('')
  const { data: options } = useAutosuggest({
    text: searchText,
    isHoofdleiding: true,
  })
  const {
    data: { items: herplanGeblokkeerdeWeken },
  } = useHerplanGeblokkeerdeWeken({
    gebiedscodes: hoofdleidingValuelists.gebieden.map((gebied) => gebied.code),
    organisatie,
  })

  const onClickMenu = useCallback((anchorEl, project) => {
    setSelectedProject(project)
    setAnchorEl(anchorEl)
  }, [])

  return (
    <Page
      title={['Projecten', 'Hoofdleiding']}
      breadcrumbs={[
        { label: 'Hoofdleiding', href: '/home', tooltip: 'CAPO home' },
        { label: 'Projecten' },
      ]}
      fillScreen
    >
      <PageSection id="projecten" heading="Overzicht van uw projecten" />

      <Paper>
        <ProjectenFilters
          filters={filters}
          initialFilters={initialFilters}
          onChange={onChangeFilters}
          options={options}
          onSearchChange={(search) => setSearchText(search)}
          onRefresh={onRefresh}
          onReset={onReset}
          filterOptions={{
            ...valuelists,
            hoofdleiding: hoofdleidingValuelists,
            labels: toFilterOptions(hoofdleidingValuelists.labels),
            processtappen: statusConfig.processtappenHoofdleiding,
          }}
          geblokkeerdeWeken={herplanGeblokkeerdeWeken}
        />
        <StyledTable
          onChangeSort={onChangeSort}
          onChangePage={onChangePage}
          headerCells={headerCells}
          sort={sort}
          stats={{ count, skip, limit }}
          loading={isLoading}
          isError={isError}
        >
          {projecten.map((project) => (
            <ProjectenTableRow
              key={project.aanvraagID}
              project={project}
              onClickMenu={onClickMenu}
            />
          ))}
        </StyledTable>
      </Paper>
      {selectedProject && (
        <ProjectOptions
          project={selectedProject}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          processtappen={statusConfig.processtappenHoofdleiding}
          geblokkeerdeWeken={herplanGeblokkeerdeWeken}
          selectableLabels={selectlists.labels}
        />
      )}
    </Page>
  )
}
